import { ArrowRightIcon, CompassIcon, MapIcon } from 'lucide-react-native';
import { useState } from 'react';
import { YStack } from 'tamagui';

import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '../../components/buttons/Button';
import { ChecklistCompletionIndicator } from '../../components/navigation/ChecklistCompletionIndicator';
import { DotNavigation } from '../../components/navigation/DotNavigation';
import {
    NavigationTile,
    NavigationTileWithSecondary,
} from '../../components/navigation/NavigationTile';
import { WorkflowStepIndicator } from '../../components/navigation/WorkflowStepIndicator';
import { BodySmallRegular } from '../../components/text/Typography';

const STEP_COUNT_A = 3;
const STEP_COUNT_B = 5;

export const Navigation = () => {
    const [currentIndexA, setCurrentIndexA] = useState(
        Math.floor(Math.random() * STEP_COUNT_A),
    );
    const [currentIndexB, setCurrentIndexB] = useState(
        Math.floor(Math.random() * STEP_COUNT_B),
    );

    const [completedSteps, setCompletedSteps] = useState([false, false, false]);

    return (
        <YStack gap={32}>
            <YStack gap={12} $sm={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                <YStack maxWidth={350}>
                    <NavigationTile
                        title="Navigation Tile"
                        description="A description for the tile"
                        iconLeft={CompassIcon}
                        iconRight={ArrowRightIcon}
                        onPress={() => {
                            console.log('NavigationTile pressed');
                        }}
                    />
                </YStack>
                <YStack maxWidth={350}>
                    <NavigationTile
                        label="Label instead of description"
                        title="Navigation Tile (positive)"
                        tone="positive"
                        iconLeft={CompassIcon}
                        iconRight={ArrowRightIcon}
                        onPress={() => {
                            console.log('NavigationTile pressed');
                        }}
                    />
                </YStack>
                <YStack maxWidth={350}>
                    <NavigationTile
                        title="Navigation Tile (warning)"
                        description="Description goes here"
                        tone="warning"
                        iconLeft={CompassIcon}
                        iconRight={ArrowRightIcon}
                        onPress={() => {
                            console.log('NavigationTile pressed');
                        }}
                    />
                </YStack>
            </YStack>
            <YStack gap={12} $sm={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                <YStack maxWidth={350}>
                    <NavigationTileWithSecondary
                        title="Navigation Tile with Secondary"
                        description="A description for the tile"
                        iconRight={ArrowRightIcon}
                        onPress={() => {
                            console.log(
                                'NavigationTileWithSecondary top pressed',
                            );
                        }}
                        secondaryTitle="Secondary action down here"
                        secondaryIcon={MapIcon}
                        secondaryOnPress={() => {
                            console.log(
                                'NavigationTileWithSecondary bottom pressed',
                            );
                        }}
                    />
                </YStack>
                <YStack maxWidth={350}>
                    <NavigationTileWithSecondary
                        tone="positive"
                        title="Navigation Tile (positive)"
                        description="A description for the tile"
                        iconRight={ArrowRightIcon}
                        onPress={() => {
                            console.log(
                                'NavigationTileWithSecondary top pressed',
                            );
                        }}
                        secondaryTitle="Secondary action down here"
                        secondaryIcon={MapIcon}
                        secondaryOnPress={() => {
                            console.log(
                                'NavigationTileWithSecondary bottom pressed',
                            );
                        }}
                    />
                </YStack>
                <YStack maxWidth={350}>
                    <NavigationTileWithSecondary
                        tone="warning"
                        title="Navigation Tile (warning)"
                        description="A description for the tile"
                        iconRight={ArrowRightIcon}
                        onPress={() => {
                            console.log(
                                'NavigationTileWithSecondary top pressed',
                            );
                        }}
                        secondaryTitle="Secondary action down here"
                        secondaryIcon={MapIcon}
                        secondaryOnPress={() => {
                            console.log(
                                'NavigationTileWithSecondary bottom pressed',
                            );
                        }}
                    />
                </YStack>
            </YStack>

            <YStack gap={16}>
                <BodySmallRegular>
                    DotNavigation provides a visual indicator for pagination or
                    multi-step content. Dots can be clicked to navigate between
                    pages.
                </BodySmallRegular>
                <YStack gap={16}>
                    <DotNavigation
                        total={3}
                        currentIndex={currentIndexA}
                        onSelectIndex={setCurrentIndexA}
                    />
                    <DotNavigation
                        total={5}
                        currentIndex={currentIndexB}
                        onSelectIndex={setCurrentIndexB}
                    />
                </YStack>
            </YStack>

            <YStack gap={16}>
                <BodySmallRegular>
                    WorkflowStepIndicator shows progress through a multi-step
                    workflow. Each line represents a step, with the current step
                    highlighted in black. It is *not* interactive, and *not* a
                    progress bar
                </BodySmallRegular>
                <YStack gap={16}>
                    <WorkflowStepIndicator
                        currentStepIndex={currentIndexA}
                        totalStepCount={STEP_COUNT_A}
                    />
                    <WorkflowStepIndicator
                        currentStepIndex={currentIndexB}
                        totalStepCount={STEP_COUNT_B}
                    />
                </YStack>
            </YStack>
            <YStack
                gap={16}
                paddingTop={16}
                borderTopWidth={1}
                borderTopColor="$gray600"
            >
                <BodySmallRegular>
                    WorkflowProgressIndicator shows progress through a
                    multi-step workflow (think of it as a checklist). Each line
                    represents a step, with completed steps highlighted. It is
                    *not* interactive.
                </BodySmallRegular>
                <YStack gap={16} maxWidth={350}>
                    <BodySmallRegular>
                        Initial state: no steps completed
                    </BodySmallRegular>
                    <ChecklistCompletionIndicator
                        indicatorColor="$green1000"
                        completedSteps={completedSteps}
                        totalStepCount={3}
                    />
                    <Button
                        onPress={() => {
                            setCompletedSteps([true, false, false]);
                        }}
                        text="Complete first step"
                        size={ButtonSize.SMALL}
                        variant={ButtonVariant.SECONDARY}
                        disabled={completedSteps[0] === true}
                    />
                    <BodySmallRegular>
                        {`Steps do not have to be completed in order (completedOrdering='in-place' (default)):`}
                    </BodySmallRegular>
                    <ChecklistCompletionIndicator
                        indicatorColor="$green1000"
                        completedSteps={[false, false, true, true, false]}
                        totalStepCount={5}
                    />
                    <BodySmallRegular>
                        {`Or they can be shown as completed from left-to-right as progress (completedOrdering='progress'):`}
                    </BodySmallRegular>
                    <ChecklistCompletionIndicator
                        indicatorColor="$green1000"
                        completedSteps={[false, false, true, true, false]}
                        totalStepCount={5}
                        completedOrdering="progress"
                    />
                </YStack>
            </YStack>
        </YStack>
    );
};
